import React, { useEffect, Dispatch, SetStateAction, useState } from "react";
import { Source, Layer } from "react-map-gl";
import { LngLat } from "react-map-gl";

import { HazardMapConfig } from "./../riskAssessment/hazardsMapConfig";
import {
  HazardIdEnum,
  HazardLayer,
  HazardLayerSecondary,
  HazardSource,
  HazardSourceSecondary,
} from "./../riskAssessment/hazardIdEnum";
import { defaultLayer } from "../riskAssessment/defaultSourceLayer";
import { defaultRiskAssessmentMapStyle } from "../map/Constants";
import { defaultSourceLayer, MapSourceData } from "./defaultSourceLayer";
import { AllowedLayerTypes } from "../map/types";

type RenderHazardMapProps = {
  setMapStyle: Function;
  selectedHazard: Hazard | null;
  sourceLayerData: MapSourceData;
  setSourceLayerData: Dispatch<SetStateAction<MapSourceData>>;
  sourceLayerDataSecondary: MapSourceData;
  setSourceLayerDataSecondary: Dispatch<SetStateAction<MapSourceData>>;
  styleLoaded?: boolean;
  isBasicMapStyle: boolean;
  setBasicMapStyle: Dispatch<SetStateAction<boolean>>;
  location?: LngLat;
};

const RenderHazardMap: React.FC<RenderHazardMapProps> = ({
  setMapStyle,
  selectedHazard,
  sourceLayerData,
  setSourceLayerData,
  sourceLayerDataSecondary,
  setSourceLayerDataSecondary,
  styleLoaded,
  isBasicMapStyle,
  setBasicMapStyle,
  location,
}) => {
  const [legendComponent, setLegendComponent] = useState<React.ReactNode>(null);

  useEffect(() => {
    if (!selectedHazard) {
      // Clear everything when no hazard selected
      setSourceLayerData(defaultSourceLayer);
      setSourceLayerDataSecondary(defaultSourceLayer);
      setLegendComponent(null);
      if (!isBasicMapStyle) {
        setMapStyle(defaultRiskAssessmentMapStyle);
      }
      return;
    }

    const config = HazardMapConfig[selectedHazard.cysId as HazardIdEnum];
    if (!config) return;

    setBasicMapStyle(false);

    // Handle legend
    if (config.legend) {
      setLegendComponent(config.legend);
    } else if (config.legendWithParams && location) {
      const Legend = config.legendWithParams;
      setLegendComponent(<Legend lat={location.lat} lng={location.lng} />);
    }

    // Handle map style
    if (config.mapStyle && !config.sourceLayer) {
      setSourceLayerData(defaultSourceLayer);
      setMapStyle(config.mapStyle);
    }

    // Handle primary source layer
    if (config.sourceLayer) {
      setMapStyle(config.mapStyle ? config.mapStyle : defaultRiskAssessmentMapStyle);

      const sourcePromise =
        (location && config.getSourceDataWithParams?.(location)) || config.getSourceData?.();

      if (sourcePromise) {
        // geojson must have getSourceData or getSourceDataWithParams
        sourcePromise.then((data: GeoJsonData | string) => {
          setSourceLayerData({
            sourceLayer: config.sourceLayer || defaultLayer,
            sourceData: data,
            layerId: `${HazardLayer}${selectedHazard.cysId}`,
            sourceId: `${HazardSource}${selectedHazard.cysId}`,
          });
        });
      } else if (config.sourceTileUrl) {
        // Must have sourceTileUrl.
        setSourceLayerData({
          sourceData: "AnythingJustNotNull",
          sourceLayer: config.sourceLayer,
          sourceTileUrl: config.sourceTileUrl,
          layerId: `${HazardLayer}${selectedHazard.cysId}`,
          sourceId: `${HazardSource}${selectedHazard.cysId}`,
        });
      } else {
        console.error(
          "Internal error: No source data function or tile URL provided for hazard",
          selectedHazard.cysId,
        );
        setSourceLayerData(defaultSourceLayer);
      }
    }

    // Handle secondary source layer
    if (config.sourceLayerSecondary && config.getSourceDataSecondary) {
      setMapStyle(config.mapStyle ? config.mapStyle : defaultRiskAssessmentMapStyle);
      config.getSourceDataSecondary().then((data: GeoJsonData) => {
        setSourceLayerDataSecondary({
          sourceLayer: config.sourceLayerSecondary || defaultLayer,
          sourceData: data,
          layerId: `${HazardLayerSecondary}${selectedHazard.cysId}`,
          sourceId: `${HazardSourceSecondary}${selectedHazard.cysId}`,
        });
      });
    } else {
      setSourceLayerDataSecondary(defaultSourceLayer);
    }
  }, [selectedHazard, location?.lat, location?.lng]);

  // Helper to check if source should render
  const shouldRenderSource = (data: MapSourceData) =>
    Object.keys(data).length > 0 &&
    data.sourceData &&
    data.sourceLayer &&
    data.sourceLayer.id !== "defaultLayer" &&
    styleLoaded;

  // Determine source props based on type
  const getSourceProps = (data: MapSourceData) => ({
    key: data.sourceId,
    id: data.sourceId,

    ...(data.sourceTileUrl
      ? {
          // For raster
          type: "raster",
          tileSize: 256,
          tiles: [data.sourceTileUrl],
        }
      : {
          // For geojson
          type: "geojson",
          data: data.sourceData,
        }),
  });

  return (
    <>
      {shouldRenderSource(sourceLayerData) && (
        <Source {...getSourceProps(sourceLayerData)}>
          <Layer
            beforeId="road-minor"
            {...(sourceLayerData.sourceLayer as AllowedLayerTypes)}
            source={sourceLayerData.sourceId}
            id={sourceLayerData.layerId}
          />
        </Source>
      )}

      {shouldRenderSource(sourceLayerDataSecondary) && (
        <Source
          key={sourceLayerDataSecondary.sourceId}
          id={sourceLayerDataSecondary.sourceId}
          type="geojson"
          data={sourceLayerDataSecondary.sourceData}
        >
          <Layer
            {...(sourceLayerDataSecondary.sourceLayer as AllowedLayerTypes)}
            source={sourceLayerDataSecondary.sourceId}
            id={sourceLayerDataSecondary.layerId}
          />
        </Source>
      )}

      {legendComponent}
    </>
  );
};

export default RenderHazardMap;
